import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import AssociateAvatar from '../../components/AssociateAvatar';
import { getAssociateDisplayLabel } from '../../utils';
import { AssignmentAssociate } from '../models';

export interface DecisionInfoModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /**
   * The associate whose assignment decision is being viewed. The decision is
   * read from `associate.decision`.
   */
  associate: AssignmentAssociate | null;
  /** Fires when the planner dismisses the modal. */
  onClose: () => void;
}

/**
 * Read-only popup showing why an associate is in their current slot: the
 * assignment {@link AssignmentDecision}'s `source` and `reasonCode` (shown
 * raw, as stored), plus the optional free-text `decisionReason` when present.
 *
 * Opened from the info (ℹ️) hover action on an occupied slot / pool card. When
 * the associate has no recorded decision (e.g. never overridden and the facade
 * sent none), a "No decision recorded" message is shown instead.
 *
 * Purely presentational and all-Meridian (Modal/Box/Row/Column/Text/Button)
 * plus the shared AssociateAvatar.
 */
const DecisionInfoModal: React.FC<DecisionInfoModalProps> = ({ open, associate, onClose }) => {
  const decision = associate?.decision;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assignment decision"
      scrollContainer="viewport"
      width="400px"
    >
      <Box data-testid="decision-info-modal">
        {associate && (
          <Row
            alignmentVertical="center"
            spacing="300"
            spacingInset="none none 400 none"
            data-testid="decision-info-associate"
          >
            <AssociateAvatar
              associateName={associate.associateName}
              associateAlias={associate.associateAlias}
            />
            <Text type="h300">
              {getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
            </Text>
          </Row>
        )}

        {decision ? (
          <Column spacing="300" data-testid="decision-info-details">
            <Column spacing="none">
              <Text type="b100" color="secondary">
                Source
              </Text>
              <Text type="b300" data-testid="decision-info-source">
                {decision.source ?? '—'}
              </Text>
            </Column>

            <Column spacing="none">
              <Text type="b100" color="secondary">
                Reason code
              </Text>
              <Text type="b300" data-testid="decision-info-reason-code">
                {decision.reasonCode ?? '—'}
              </Text>
            </Column>

            {decision.decisionReason && (
              <Column spacing="none">
                <Text type="b100" color="secondary">
                  Reason
                </Text>
                <Text type="b300" data-testid="decision-info-reason">
                  {decision.decisionReason}
                </Text>
              </Column>
            )}
          </Column>
        ) : (
          <Text type="b300" color="secondary" data-testid="decision-info-empty">
            No decision recorded.
          </Text>
        )}
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button
            type="primary"
            size="small"
            onClick={onClose}
            data-testid="decision-info-close-button"
          >
            Close
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default DecisionInfoModal;
